import {
  placeBlockWithRules,
  refreshConnectionsAround
} from "../core/blockPlacement.ts";

function carveGateArch(world, centerX, frontZ, backZ) {
  for (let z = frontZ; z <= backZ; z += 1) {
    for (let y = 1; y <= 6; y += 1) {
      for (let x = centerX - 3; x <= centerX + 3; x += 1) {
        world.removeBlock(x, y, z);
      }
    }

    for (let x = centerX - 2; x <= centerX + 2; x += 1) {
      world.removeBlock(x, 7, z);
    }

    for (let x = centerX - 1; x <= centerX + 1; x += 1) {
      world.removeBlock(x, 8, z);
    }

    world.removeBlock(centerX, 9, z);
  }
}

function addBattlements(world, x1, x2, y, z) {
  for (let x = x1; x <= x2; x += 2) {
    world.fill(x, y, z, x, y + 1, z, "stone_bricks");
  }
}

function addTowerWindows(world, centerX, centerZ) {
  const windowLevels = [7, 14];

  windowLevels.forEach(function (y) {
    [-1, 0, 1].forEach(function (offset) {
      world.removeBlock(centerX + offset, y, centerZ - 8);
      world.removeBlock(centerX + offset, y + 1, centerZ - 8);
      world.removeBlock(centerX + offset, y, centerZ + 8);
      world.removeBlock(centerX + offset, y + 1, centerZ + 8);
    });

    [-1, 0, 1].forEach(function (offset) {
      world.removeBlock(centerX - 8, y, centerZ + offset);
      world.removeBlock(centerX - 8, y + 1, centerZ + offset);
      world.removeBlock(centerX + 8, y, centerZ + offset);
      world.removeBlock(centerX + 8, y + 1, centerZ + offset);
    });

    world.setBlock(centerX, y, centerZ - 8, "iron_bars");
    world.setBlock(centerX, y + 1, centerZ - 8, "iron_bars");
    world.setBlock(
      centerX,
      y,
      centerZ + 8,
      "glass_pane"
    );
    world.setBlock(
      centerX,
      y + 1,
      centerZ + 8,
      "glass_pane"
    );
  });
}

function addTowerRoof(world, centerX, centerZ) {
  for (let layer = 0; layer < 7; layer += 1) {
    const radius = Math.max(2, 8 - layer);
    const y = 21 + layer;

    world.cylinder(
      centerX,
      y,
      centerZ,
      Math.max(1, radius - 1),
      1,
      "deepslate_tiles",
      false
    );

    for (let x = centerX - radius; x <= centerX + radius; x += 1) {
      world.setBlock(
        x,
        y,
        centerZ - radius,
        "deepslate_tile_stairs",
        {
          facing: "north",
          half: "bottom",
          shape: "straight",
          waterlogged: "false"
        }
      );
      world.setBlock(
        x,
        y,
        centerZ + radius,
        "deepslate_tile_stairs",
        {
          facing: "south",
          half: "bottom",
          shape: "straight",
          waterlogged: "false"
        }
      );
    }

    for (
      let z = centerZ - radius + 1;
      z <= centerZ + radius - 1;
      z += 1
    ) {
      world.setBlock(
        centerX - radius,
        y,
        z,
        "deepslate_tile_stairs",
        {
          facing: "west",
          half: "bottom",
          shape: "straight",
          waterlogged: "false"
        }
      );
      world.setBlock(
        centerX + radius,
        y,
        z,
        "deepslate_tile_stairs",
        {
          facing: "east",
          half: "bottom",
          shape: "straight",
          waterlogged: "false"
        }
      );
    }
  }

  world.fill(
    centerX - 2,
    28,
    centerZ - 2,
    centerX + 2,
    28,
    centerZ + 2,
    "deepslate_tile_slab",
    { type: "top", waterlogged: "false" }
  );

  world.fill(
    centerX,
    29,
    centerZ,
    centerX,
    32,
    centerZ,
    "dark_oak_log"
  );
  world.setBlock(
    centerX,
    33,
    centerZ,
    "lantern",
    { hanging: "false", waterlogged: "false" }
  );
}

function addTimberBand(world, centerX, centerZ) {
  for (let y = 5; y <= 18; y += 7) {
    for (let x = centerX - 7; x <= centerX + 7; x += 1) {
      world.setBlock(x, y, centerZ - 8, "spruce_planks");
      world.setBlock(x, y, centerZ + 8, "spruce_planks");
    }
  }

  const cornerOffsets = [
    [-7, -6],
    [7, -6],
    [-7, 6],
    [7, 6]
  ];

  cornerOffsets.forEach(function (offset) {
    world.fill(
      centerX + offset[0],
      2,
      centerZ + offset[1],
      centerX + offset[0],
      19,
      centerZ + offset[1],
      "spruce_log"
    );
  });
}

function addButtress(world, x, z, directionX, directionZ) {
  world.fill(x, 1, z, x + directionX * 2, 7, z + directionZ * 2, "polished_andesite");
  world.fill(
    x + directionX,
    8,
    z + directionZ,
    x + directionX,
    11,
    z + directionZ,
    "polished_andesite"
  );
}

function addSmallTree(world, x, z, height) {
  world.fill(x, 1, z, x, height, z, "dark_oak_log");
  world.sphere(x, height + 2, z, 3, "oak_leaves", false);
  world.sphere(x + 2, height + 1, z, 2, "oak_leaves", false);
  world.sphere(x - 2, height + 1, z + 1, 2, "oak_leaves", false);
}

function weatherStone(world) {
  const candidates = world.entries().filter(function (entry) {
    return entry.blockId === "stone_bricks" && entry.y > 1;
  });

  candidates.forEach(function (entry) {
    const deterministicHash = Math.abs(
      entry.x * 73856093 ^
      entry.y * 19349663 ^
      entry.z * 83492791
    ) % 100;

    if (deterministicHash < 5) {
      world.setBlock(entry.x, entry.y, entry.z, "mossy_stone_bricks");
    } else if (deterministicHash < 11) {
      world.setBlock(entry.x, entry.y, entry.z, "cracked_stone_bricks");
    }
  });
}

export function generateMedievalGatehouse(world) {
  world.beginTransaction("Generate medieval gatehouse");

  try {
    world.runBatch(function () {
      world.fill(-36, 0, -38, 36, 0, 34, "grass_block");

      for (let z = -38; z <= 34; z += 1) {
        const roadHalfWidth = z < -18 ? 5 : 4;
        world.fill(-roadHalfWidth, 0, z, roadHalfWidth, 0, z, "gravel");
      }

      world.fill(-36, 0, -18, 36, 0, -12, "water");
      world.fill(-5, 0, -19, 5, 0, -11, "spruce_planks");
      world.fill(-6, 1, -18, -6, 2, -12, "dark_oak_log");
      world.fill(6, 1, -18, 6, 2, -12, "dark_oak_log");

      [-13, 13].forEach(function (towerX) {
        world.cylinder(towerX, 1, 0, 8, 20, "stone_bricks", true);
        world.cylinder(towerX, 1, 0, 6, 1, "cobblestone", false);
        world.cylinder(towerX, 19, 0, 8, 2, "polished_andesite", true);
        addTowerWindows(world, towerX, 0);
        addTimberBand(world, towerX, 0);
        addTowerRoof(world, towerX, 0);
      });

      world.hollowBox(-7, 1, -6, 7, 19, 6, "stone_bricks", 1);
      world.fill(-6, 2, -5, 6, 2, 5, "spruce_planks");
      world.fill(-6, 10, -5, 6, 10, 5, "spruce_planks");
      world.fill(-6, 16, -5, 6, 16, 5, "spruce_planks");

      carveGateArch(world, 0, -7, 7);

      for (let x = -6; x <= 6; x += 3) {
        world.fill(x, 3, -6, x, 17, -6, "spruce_log");
        world.fill(x, 3, 6, x, 17, 6, "spruce_log");
      }

      world.fill(-7, 19, -7, 7, 19, 7, "polished_andesite");
      world.fill(
        -6,
        20,
        -6,
        6,
        20,
        6,
        "deepslate_tile_slab",
        { type: "top", waterlogged: "false" }
      );

      for (let x = -6; x <= 6; x += 1) {
        world.setBlock(
          x,
          20,
          -7,
          "deepslate_tile_stairs",
          {
            facing: "north",
            half: "bottom",
            shape: "straight",
            waterlogged: "false"
          }
        );
        world.setBlock(
          x,
          20,
          7,
          "deepslate_tile_stairs",
          {
            facing: "south",
            half: "bottom",
            shape: "straight",
            waterlogged: "false"
          }
        );
      }
      addBattlements(world, -7, 7, 20, -7);
      addBattlements(world, -7, 7, 20, 7);

      for (let z = -5; z <= 5; z += 2) {
        world.fill(-7, 20, z, -7, 21, z, "stone_bricks");
        world.fill(7, 20, z, 7, 21, z, "stone_bricks");
      }

      world.fill(-2, 12, -6, 2, 15, -6, "calcite");
      world.fill(-1, 13, -7, 1, 14, -7, "deepslate_tiles");
      world.setBlock(
        0,
        12,
        -7,
        "lantern",
        { hanging: "true", waterlogged: "false" }
      );
      world.setBlock(
        -4,
        6,
        -7,
        "lantern",
        { hanging: "false", waterlogged: "false" }
      );
      world.setBlock(
        4,
        6,
        -7,
        "lantern",
        { hanging: "false", waterlogged: "false" }
      );

      placeBlockWithRules(
        world,
        { x: -5, y: 3, z: 6 },
        "spruce_door",
        {
          facing: "south",
          hinge: "left",
          open: "false",
          powered: "false"
        }
      );

      for (let x = -6; x <= 6; x += 2) {
        placeBlockWithRules(
          world,
          { x, y: 18, z: 7 },
          "spruce_fence",
          {}
        );
      }

      [-5, 5].forEach(function (x) {
        world.setBlock(
          x,
          11,
          -7,
          "spruce_trapdoor",
          {
            facing: "north",
            half: "bottom",
            open: "true",
            powered: "false",
            waterlogged: "false"
          }
        );
      });

      world.hollowBox(-35, 1, -5, -21, 10, 5, "stone_bricks", 1);
      world.hollowBox(21, 1, -5, 35, 10, 5, "stone_bricks", 1);

      for (let x = -35; x <= -21; x += 2) {
        world.fill(x, 11, -5, x, 12, -5, "stone_bricks");
        world.fill(x, 11, 5, x, 12, 5, "stone_bricks");
      }
      for (let x = 21; x <= 35; x += 2) {
        world.fill(x, 11, -5, x, 12, -5, "stone_bricks");
        world.fill(x, 11, 5, x, 12, 5, "stone_bricks");
      }

      [-22, 22].forEach(function (x) {
        addButtress(world, x, -6, x < 0 ? -1 : 1, -1);
        addButtress(world, x, 6, x < 0 ? -1 : 1, 1);
      });

      for (let z = 10; z <= 27; z += 8) {
        world.setBlock(-7, 1, z, "lantern");
        world.setBlock(7, 1, z, "lantern");
        world.fill(-7, 0, z, -7, 0, z, "polished_andesite");
        world.fill(7, 0, z, 7, 0, z, "polished_andesite");
      }

      addSmallTree(world, -25, 18, 5);
      addSmallTree(world, 25, 20, 5);
      addSmallTree(world, -18, 27, 4);
      addSmallTree(world, 19, 29, 4);

      world.sphere(-11, 2, 21, 2, "oak_leaves", false);
      world.sphere(11, 2, 23, 2, "oak_leaves", false);
      world.sphere(-16, 2, 13, 2, "oak_leaves", false);
      world.sphere(16, 2, 14, 2, "oak_leaves", false);

      refreshConnectionsAround(world, world.entries());
      weatherStone(world);
    });

    return world.commitTransaction();
  } catch (error) {
    world.cancelTransaction();
    throw error;
  }
}
